// Route-chunk load resilience + observability.
//
// Problem: this app is code-split (React.lazy). After any redeploy, a client that
// still holds the OLD index.html will request an OLD chunk hash that no longer
// exists on the CDN -> the dynamic import() rejects with a ChunkLoadError and the
// route silently white-screens with no signal. (Registered as assumption A2 in
// e2e/PHASE2-PROOF.md.)
//
// Fix: a single global guard that (1) emits a beacon so the failure is observable,
// and (2) does ONE hard reload to fetch the fresh index.html + current chunk
// hashes. A sessionStorage flag prevents reload loops if the reload itself fails.
//
// Pure functions are exported for testing; installChunkReloadGuard() wires them to
// window. Nothing here throws — a guard that crashes would be worse than the bug.

const RELOAD_FLAG = "wtfgo_chunk_reloaded_at";
const RELOAD_LOOP_WINDOW_MS = 10000; // if we reloaded <10s ago, don't reload again

// A chunk-load failure looks like one of these across browsers/bundlers.
export function isChunkLoadError(err) {
  if (!err) return false;
  const name = err.name || "";
  const msg = err.message || String(err) || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /import\(\) .*failed/i.test(msg) ||
    /dynamically imported module/i.test(msg)
  );
}

// Decide what to do for a given error. Returns the action taken as a string so it
// is assertable in tests. Side effects go through injected deps (no globals here).
export function handleChunkError(err, deps) {
  const { storage, reload, beacon, now } = deps;
  if (!isChunkLoadError(err)) return "ignored";

  const ts = now ? now() : Date.now();
  const last = Number(storage.getItem(RELOAD_FLAG) || 0);

  // Beacon first — the failure must be observable even if we then reload.
  try { beacon({ type: "chunk_load_error", message: err && err.message, at: ts }); } catch (_) { /* never throw */ }

  // Loop guard: if we already reloaded very recently, the reload didn't fix it
  // (offline / genuinely-missing asset). Stop reloading; leave the beacon + let
  // the Suspense/error UI show. Do NOT wedge the user in a refresh loop.
  if (last && ts - last < RELOAD_LOOP_WINDOW_MS) return "suppressed_loop";

  storage.setItem(RELOAD_FLAG, String(ts));
  reload();
  return "reloaded";
}

// Wire the pure handler to real browser globals. Safe to call once at startup.
export function installChunkReloadGuard(win = typeof window !== "undefined" ? window : undefined) {
  if (!win || win.__wtfgoChunkGuardInstalled) return false;
  win.__wtfgoChunkGuardInstalled = true;

  const deps = {
    storage: win.sessionStorage,
    reload: () => win.location.reload(),
    beacon: (payload) => {
      // Lightweight, dependency-free signal. Picked up by console log drains /
      // Chrome console reader; upgrade to a real metrics endpoint when one exists.
      // eslint-disable-next-line no-console
      console.error("[wtfgo:chunk] " + JSON.stringify(payload));
    },
    now: () => Date.now(),
  };

  win.addEventListener("error", (e) => {
    // Resource/script load errors surface on window 'error' with e.error possibly null.
    handleChunkError(e && (e.error || { message: e && e.message }), deps);
  });
  win.addEventListener("unhandledrejection", (e) => {
    handleChunkError(e && e.reason, deps);
  });
  return true;
}
